import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosClientAdmin } from '@/lib/auth'; // Assuming this is your admin axios client
import { MonitoringTeam } from './types'; // Import your actual MonitoringTeam type
import { Spinner } from '@/components/ui/spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { Badge } from '@/components/ui/badge';
import { TeamAgentsTab } from './TeamAgentsTab';
import { TeamShiftsTab } from './TeamShiftsTab';
import { Users, Save, Clock, Calendar, Plus, Construction } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEffect, useState } from 'react';
import { updateCompanyTenantMonitoring } from './teamsApi';

async function fetchMonitoringTeamById(tenantId: string, teamId: string): Promise<MonitoringTeam> {
  const response = await axiosClientAdmin.get<{ monitoringCompany: MonitoringTeam }>(
    `/tenants/${tenantId}/TenantMonitoring/companies/${teamId}`
  );
  return response.data.monitoringCompany;
}

export function TeamDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId, teamId } = useParams<{ tenantId: string; teamId: string }>();

  const {
    data: team,
    isLoading,
    error,
    isError,
  } = useQuery<MonitoringTeam, Error>({
    queryKey: ['monitoringTeam', tenantId, teamId],
    queryFn: () => fetchMonitoringTeamById(tenantId!, teamId!),
    enabled: !!tenantId && !!teamId, // Only run query if teamId is available
  });

  // Editable state

  const [editIsDefault, setEditIsDefault] = useState<boolean>(team?.isDefault ?? false);
  const [editDefaultOrder, setEditDefaultOrder] = useState<number | undefined>(team?.defaultOrder);
  const [isDirty, setIsDirty] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    setEditIsDefault(team?.isDefault ?? false);
    setEditDefaultOrder(team?.defaultOrder);
    setIsDirty(false);
    setErrorMsg(null);
  }, [team]);

  function handleChange(field: 'isDefault' | 'defaultOrder', value: boolean | number | undefined) {
    setIsDirty(true);
    setErrorMsg(null);
    if (field === 'isDefault') {
      setEditIsDefault(value as boolean);
    } else if (field === 'defaultOrder') {
      setEditDefaultOrder(value as number | undefined);
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      if (!tenantId || !teamId) throw new Error('Missing tenant or team ID');
      return updateCompanyTenantMonitoring({
        tenantId,
        companyId: teamId,
        body: {
          ...team,
          isDefault: editIsDefault,
          defaultOrder: editDefaultOrder,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monitoringTeam', tenantId, teamId] });
      setIsDirty(false);
      setErrorMsg(null);
    },
    onError: (err: unknown) => {
      setErrorMsg((err as Error)?.message || 'Failed to update team');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (isError || !team) {
    return (
      <div className="flex h-screen flex-col items-center justify-center text-white">
        <p className="text-xl">Error fetching team details.</p>
        {error && <p className="text-red-500">{error.message}</p>}
        {!team && !isLoading && <p>Team not found.</p>}
        <Button onClick={() => navigate('/team')} className="mt-4">
          Back to Team List
        </Button>
      </div>
    );
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate();
  }

  function handleCancel() {
    setEditIsDefault(team?.isDefault ?? false);
    setEditDefaultOrder(team?.defaultOrder);
    setIsDirty(false);
    setErrorMsg(null);
  }

  return (
    <>
      <div className="">
    
        <div className="sticky top-14 z-[20] mx-auto flex items-center justify-between border-b bg-[#111] p-4" data-onboarding="team-detail-header">
          <div className="flex items-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-lime/20 text-lime">
              <Users className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <CardTitle className="text-2xl font-semibold">{team.name}</CardTitle>
              <p className="text-sm text-white">{team.primaryEmail || 'No contact email'}</p>
            </div>
          </div>
          <div className="mr-3 flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={!isDirty || mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="flex items-center gap-2 bg-lime text-black hover:bg-lime/90"
              onClick={handleSave}
              disabled={!isDirty || mutation.isPending}
            >
              <Save className="h-4 w-4" />
              {mutation.isPending ? 'Saving...' : 'Save Team'}
            </Button>
            {errorMsg && <span className="ml-4 text-sm text-red-500">{errorMsg}</span>}
          </div>
        </div>
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="w-full flex-1 border-x-0 border-t-0 bg-transparent" data-onboarding="team-detail-tabs">
            <TabsTrigger
              value="details"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              Team Details
            </TabsTrigger>
            <TabsTrigger
              value="members"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              Team Members
            </TabsTrigger>

            <TabsTrigger
              value="shifts"
              className="data-[state=active]:bg-background data-[state=active]:text-lime"
            >
              Recent Shifts
            </TabsTrigger>
          </TabsList>

          <div className="">
            <TabsContent value="details">
              <section className="mx-auto mt-12 grid max-w-[1280px] grid-cols-[320px_1fr] gap-6">
                <div>
                  <div className="sticky top-36 flex flex-col gap-2 font-semibold text-lime">
                    <a href="#team">Team Details</a>
                    <a href="#sites">Site Access</a>
                  </div>
                </div>

                <form
                  onSubmit={(e) => e.preventDefault()} /* TODO: Re-enable/refactor handleSubmit */
                >
                  <div className="grid grid-cols-1 gap-6">
                    {/* Basic Information */}
                    <Card className="border-white/10" data-onboarding="team-detail-info">
                      <CardHeader>
                        <CardTitle className="text-white">Basic Information</CardTitle>
                        <CardDescription>Team profile and details</CardDescription>
                      </CardHeader>
                      <CardContent className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <span className="block text-xs uppercase tracking-wide">Team Name</span>
                          <span className="block text-lg font-semibold">{team.name}</span>
                        </div>
                        <div className="space-y-1">
                          <span className="block text-xs uppercase tracking-wide">
                            Contact Email
                          </span>
                          <span className="block text-base text-gray-400">
                            {team.primaryEmail || '—'}
                          </span>
                        </div>
                        {/* Is Default toggle */}
                        <div className="space-y-2">
                          <Label htmlFor="isDefault" className="text-white">
                            Is Default
                          </Label>
                          <div className="flex items-center gap-3">
                            <Switch
                              id="isDefault"
                              checked={editIsDefault}
                              onCheckedChange={(v) => handleChange('isDefault', v)}
                              className="data-[state=checked]:bg-lime/20"
                            />
                            <span className="text-sm text-white/80">
                              {editIsDefault ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                        {/* Default Order */}
                        <div className="space-y-2">
                          <Label htmlFor="defaultOrder" className="text-white">
                            Default Order
                          </Label>
                          <Input
                            id="defaultOrder"
                            type="number"
                            min={0}
                            value={editDefaultOrder ?? ''}
                            onChange={(e) =>
                              handleChange(
                                'defaultOrder',
                                e.target.value === '' ? undefined : Number(e.target.value)
                              )
                            }
                            className="bg-gray-800 text-white"
                          />
                        </div>
                      </CardContent>
                    </Card>

                    {/* TODO: Refactor Permissions & Access Control Card */}
                    <Card className="border-white/10" id="sites">
                      <CardHeader>
                        <CardTitle className="text-white">Site Access</CardTitle>
                        <CardDescription>Team site access</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-card2 text-lime">
                            <Construction className="h-8 w-8" />
                          </div>
                          <h3 className="mb-2 text-xl font-semibold text-white">Coming Soon</h3>
                          <p className="mb-4 max-w-md text-white/70">
                            We're currently building site access management features for teams.
                            Check back soon for updates.
                          </p>
                          <div className="flex items-center text-sm text-white/50">
                            <Clock className="mr-1 h-4 w-4" />
                            <span>Expected Q3 2025</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* TODO: Refactor Team Schedule Card */}
                  </div>
                </form>
              </section>
            </TabsContent>
            <TabsContent value="members" className="">
              {tenantId && teamId && <TeamAgentsTab tenantId={tenantId} teamId={teamId} />}
            </TabsContent>

            <TabsContent value="shifts" className="mt-0">
              {tenantId && teamId && <TeamShiftsTab />}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </>
  );
}

export default TeamDetailPage;
